import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Loader2, Save, Copy, Percent, GraduationCap, Trophy } from "lucide-react";
import { toast } from "sonner";

const DEF_REMARKS = [
  { min: 91, max: 100, remark: "Outstanding" }, { min: 75, max: 90, remark: "Excellent" },
  { min: 56, max: 74, remark: "Very Good" }, { min: 41, max: 55, remark: "Good" },
  { min: 33, max: 40, remark: "Satisfactory" }, { min: 0, max: 32, remark: "Needs Improvement" },
];
const DEF_GRADES = [
  { min: 91, max: 100, grade: "A1" }, { min: 81, max: 90, grade: "A2" }, { min: 71, max: 80, grade: "B1" },
  { min: 61, max: 70, grade: "B2" }, { min: 51, max: 60, grade: "C1" }, { min: 41, max: 50, grade: "C2" },
  { min: 33, max: 40, grade: "D" }, { min: 0, max: 32, grade: "E" },
];
const DEF_DIVISIONS = [
  { min: 60, max: 100, name: "First Division" }, { min: 45, max: 59, name: "Second Division" },
  { min: 33, max: 44, name: "Third Division" }, { min: 0, max: 32, name: "Fail" },
];

function RangeTable({ title, icon: Icon, rows, setRows, labelKey, labelName, testid, readOnly }) {
  const upd = (i, k, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2 text-sm"><Icon size={16} className="text-indigo-500" />{title}</h3>
        {!readOnly && <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => setRows([...rows, { min: 0, max: 0, [labelKey]: "" }])} data-testid={`${testid}-add`}><Plus size={13} />Add</Button>}
      </div>
      <div className="grid grid-cols-[1fr_1fr_1.4fr_auto] gap-2 text-[11px] font-medium text-slate-400 uppercase mb-1 px-1">
        <span>From %</span><span>To %</span><span>{labelName}</span><span></span>
      </div>
      <div className="space-y-1.5">
        {rows.length === 0 && <p className="text-xs text-slate-400 py-2">No rows. Click Add.</p>}
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-center" data-testid={`${testid}-row-${i}`}>
            <Input type="number" value={r.min} onChange={(e) => upd(i, "min", e.target.value)} className="h-8" />
            <Input type="number" value={r.max} onChange={(e) => upd(i, "max", e.target.value)} className="h-8" />
            <Input value={r[labelKey] ?? ""} onChange={(e) => upd(i, labelKey, e.target.value)} className="h-8" placeholder={labelName} />
            <button onClick={() => setRows(rows.filter((_, idx) => idx !== i))} className="text-rose-400 hover:text-rose-600 p-1" data-testid={`${testid}-del-${i}`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MarksGradeTab({ session, sessions }) {
  const { user } = useAuth();
  const canManage = hasPerm(user, "manage_exams");
  const [remarks, setRemarks] = useState([]);
  const [grades, setGrades] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!session) return;
    setLoading(true);
    api.get(`/exam-settings?session=${encodeURIComponent(session)}`).then((r) => {
      setRemarks(r.data.percentage_remarks?.length ? r.data.percentage_remarks : DEF_REMARKS);
      setGrades(r.data.subject_grades?.length ? r.data.subject_grades : DEF_GRADES);
      setDivisions(r.data.divisions?.length ? r.data.divisions : DEF_DIVISIONS);
    }).finally(() => setLoading(false));
  }, [session]);
  useEffect(() => { load(); }, [load]);

  const norm = (rows, labelKey) => rows.map((r) => ({ min: Number(r.min) || 0, max: Number(r.max) || 0, [labelKey]: (r[labelKey] || "").trim() }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/exam-settings", {
        session,
        percentage_remarks: norm(remarks, "remark"),
        subject_grades: norm(grades, "grade"),
        divisions: norm(divisions, "name"),
      });
      toast.success("Marks & grade settings saved");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const copyFrom = async (src) => {
    if (!src || src === session) return;
    try {
      const r = await api.get(`/exam-settings?session=${encodeURIComponent(src)}`);
      const has = (r.data.percentage_remarks?.length || r.data.subject_grades?.length || r.data.divisions?.length);
      if (!has) { toast.info(`Session ${src} has no saved settings to copy.`); return; }
      setRemarks(r.data.percentage_remarks?.length ? r.data.percentage_remarks : remarks);
      setGrades(r.data.subject_grades?.length ? r.data.subject_grades : grades);
      setDivisions(r.data.divisions?.length ? r.data.divisions : divisions);
      toast.success(`Copied settings from ${src}. Review and Save to apply.`);
    } catch (e) { toast.error("Could not copy settings"); }
  };

  const otherSessions = (sessions || []).filter((s) => s.name !== session);

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-4" data-testid="marks-grade-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-600">Grading & remark ranges for session <b>{session || "—"}</b>.</p>
        <div className="flex items-center gap-2">
          {canManage && otherSessions.length > 0 && (
            <Select onValueChange={copyFrom}>
              <SelectTrigger className="w-52 h-9" data-testid="mg-copy-from"><Copy size={14} className="mr-1 text-slate-400" /><SelectValue placeholder="Copy Settings From…" /></SelectTrigger>
              <SelectContent>{otherSessions.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {canManage && <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="mg-save"><Save size={15} />{saving ? "Saving…" : "Save"}</Button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <RangeTable title="Percentage Remarks Range" icon={Percent} rows={remarks} setRows={setRemarks} labelKey="remark" labelName="Remark" testid="mg-remarks" readOnly={!canManage} />
        <RangeTable title="Subject Grade Range" icon={GraduationCap} rows={grades} setRows={setGrades} labelKey="grade" labelName="Grade" testid="mg-grades" readOnly={!canManage} />
      </div>
      <div className="max-w-xl">
        <RangeTable title="Divisions" icon={Trophy} rows={divisions} setRows={setDivisions} labelKey="name" labelName="Division" testid="mg-divisions" readOnly={!canManage} />
      </div>
    </div>
  );
}
